// Submit a generated try-on to admin review for the "Featured" feed.
// Costs 3 credits (or a free 7-day-streak pass). Approval happens within 24h.
//
// Requirements enforced here (Play Store + GDPR/DPDP-friendly):
//   - Caption: ≤3 words OR ≤3 hashtags (no mix)
//   - Optional Instagram handle so brands / stylists can DM the creator
//   - 4 consent checkboxes; submission disabled until required ones are ticked
import { useEffect, useState } from "react";
import { Sparkles, Loader2, Coins, Instagram, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useCredits } from "@/hooks/useCredits";
import {
  validateFeaturedCaption,
  captionErrorMessage,
  normalizeInstagramHandle,
} from "@/lib/featured-caption";
import { isNativeApp } from "@/lib/platform";
import { GetTheAppDialog } from "@/components/GetTheAppDialog";

interface Props {
  tryonId: string | null;
  onClose: () => void;
  onSubmitted?: () => void;
}

const ROW = "flex items-start gap-2.5 text-[12.5px] leading-snug";

export const SubmitFeaturedSheet = ({ tryonId, onClose, onSubmitted }: Props) => {
  const open = !!tryonId;
  // Web (desktop + mobile web) — Get Featured is Android-app-only.
  if (open && !isNativeApp()) {
    return (
      <GetTheAppDialog
        open={open}
        onOpenChange={(o) => { if (!o) onClose(); }}
        title="Get Featured in the app"
        description="Submitting featured looks is available in the FitAuraAI Android app."
      />
    );
  }
  const [caption, setCaption] = useState("");
  const [ig, setIg] = useState("");
  const [showIg, setShowIg] = useState(true);
  const [contentRights, setContentRights] = useState(false);
  const [publicDisplay, setPublicDisplay] = useState(false);
  const [igOutreach, setIgOutreach] = useState(false);
  const [ageOk, setAgeOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { credits, refresh } = useCredits();


  useEffect(() => {
    if (open) {
      setCaption("");
      setIg("");
      setShowIg(true);
      setContentRights(false);
      setPublicDisplay(false);
      setIgOutreach(false);
      setAgeOk(false);
    }
  }, [open, tryonId]);

  const captionCheck = validateFeaturedCaption(caption);
  const igTrimmed = ig.trim();
  const igNormalized = igTrimmed ? normalizeInstagramHandle(igTrimmed) : null;
  const igInvalid = !!igTrimmed && !igNormalized;
  const hasEnough = (credits ?? 0) >= 3;

  const requiredConsents = contentRights && publicDisplay && ageOk;
  const igConsentOk = !igNormalized || igOutreach;
  const canSubmit =
    !submitting &&
    hasEnough &&
    captionCheck.ok &&
    !igInvalid &&
    requiredConsents &&
    igConsentOk;

  const onSubmit = async () => {
    if (!tryonId || !canSubmit) return;
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("submit-featured", {
      body: {
        tryon_id: tryonId,
        caption: caption.trim(),
        instagram_handle: igNormalized,
        show_instagram: showIg,
        consents: {
          content_rights: contentRights,
          public_display: publicDisplay,
          instagram_outreach: igOutreach,
          age_confirmed: ageOk,
        },
      },
    });
    setSubmitting(false);
    // supabase.functions.invoke returns data=null on non-2xx (FunctionsHttpError).
    // Read the response body off the error to surface real codes like already_submitted / insufficient_credits.
    let code = (data?.error as string) ?? "";
    if (!code && error) {
      try {
        const ctx = (error as unknown as { context?: Response }).context;
        if (ctx && typeof ctx.json === "function") {
          const body = await ctx.json();
          code = (body?.error as string) ?? "";
        }
      } catch { /* ignore */ }
    }
    if (error || code) {
      if (code === "insufficient_credits") toast.error("You need 3 credits to submit (or a 7-day streak pass).");
      else if (code === "already_submitted") toast.message("Already submitted", { description: "You already submitted this look for featuring. Pick a different one." });
      else if (code === "consent_required") toast.error("Please accept the required consents.");
      else if (code === "instagram_consent_required") toast.error("Please confirm Instagram outreach consent.");
      else if (code === "bad_instagram_handle") toast.error("That Instagram handle looks invalid.");
      else if (code.startsWith("caption_")) toast.error("Caption must be ≤3 words or ≤3 hashtags.");
      else toast.error("Couldn't submit. Try again.");
      return;
    }
    await refresh();
    onSubmitted?.();
    onClose();
    toast.success("Submitted for review", {
      description: "Within 24 hours an admin will review your look. If approved, it appears in the Featured feed.",
      duration: 6000,
    });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl border-t border-white/10 max-h-[92vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Submit to Featured
          </SheetTitle>
          <SheetDescription>
            Approved looks show up on the Featured reels for everyone. Brands and stylists may reach out on Instagram.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Caption */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Caption (optional) — 3 words OR 3 #tags
            </label>
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 60))}
              placeholder="#summer #linen #vibes"
              maxLength={60}
            />
            <div className="flex items-center justify-between text-[10px]">
              <span className={captionCheck.ok ? "text-muted-foreground" : "text-destructive"}>
                {captionCheck.ok ? "Looks great." : captionErrorMessage(captionCheck.reason)}
              </span>
              <span className="text-muted-foreground">{caption.length}/60</span>
            </div>
          </div>

          {/* Instagram */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Instagram handle (optional)
            </label>
            <div className="relative">
              <Instagram className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={ig}
                onChange={(e) => setIg(e.target.value.slice(0, 60))}
                placeholder="@yourhandle"
                className="pl-9"
              />
            </div>
            <p className="text-[10px] text-muted-foreground">
              Brands and stylists may DM you here if they like your look. Leave blank to stay private.
            </p>
            {igInvalid && (
              <p className="text-[10px] text-destructive">That handle looks invalid (letters, numbers, dots, underscores only).</p>
            )}
            {igNormalized && (
              <label className="mt-1 flex items-center gap-2 text-[12px]">
                <Checkbox checked={showIg} onCheckedChange={(v) => setShowIg(!!v)} />
                <span>Show <span className="font-semibold">@{igNormalized}</span> on my featured card</span>
              </label>
            )}
          </div>

          {/* Consent */}
          <div className="rounded-2xl border border-white/10 bg-muted/30 p-3 space-y-2.5">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Consent
            </p>
            <label className={ROW}>
              <Checkbox checked={contentRights} onCheckedChange={(v) => setContentRights(!!v)} className="mt-0.5" />
              <span>This is my photo, or I have full rights to share it.</span>
            </label>
            <label className={ROW}>
              <Checkbox checked={publicDisplay} onCheckedChange={(v) => setPublicDisplay(!!v)} className="mt-0.5" />
              <span>I agree FitAura can publicly display this image inside the app for other users.</span>
            </label>
            {igNormalized && (
              <label className={ROW}>
                <Checkbox checked={igOutreach} onCheckedChange={(v) => setIgOutreach(!!v)} className="mt-0.5" />
                <span>I agree to display my Instagram handle publicly so others can reach out.</span>
              </label>
            )}
            <label className={ROW}>
              <Checkbox checked={ageOk} onCheckedChange={(v) => setAgeOk(!!v)} className="mt-0.5" />
              <span>I am 13 years or older.</span>
            </label>
          </div>

          <div className="rounded-xl border border-white/10 bg-muted/20 p-3 text-xs text-muted-foreground">
            <p className="flex items-center gap-1.5 font-medium text-foreground">
              <Coins className="h-3.5 w-3.5 text-primary" /> Cost: 3 credits
            </p>
            <p className="mt-1">Free if you have a 7-day streak Featured pass. Admin review within ~24 hours — you'll be notified in the app.</p>
          </div>

          <Button
            className="h-12 w-full rounded-full gradient-primary text-primary-foreground disabled:opacity-60"
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>
            ) : !hasEnough ? (
              "Need 3 credits"
            ) : !requiredConsents ? (
              "Accept consents to continue"
            ) : (
              <><Sparkles className="mr-2 h-4 w-4" /> Submit · 3 credits</>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
